
import React, { useState, useRef, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { isBrowser } from "@/utils/runtime";

interface ImageComparisonSliderProps {
  beforeImage: string;
  afterImage: string;
  beforeLabel?: string;
  afterLabel?: string;
}

const ImageComparisonSlider: React.FC<ImageComparisonSliderProps> = ({
  beforeImage,
  afterImage,
  beforeLabel = "Antes",
  afterLabel = "Depois"
}) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const animationFrame = useRef<number>();

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
  };

  const handleTouchStart = () => {
    isDragging.current = true;
  };

  const calculatePosition = (clientX: number) => {
    if (!containerRef.current) return;
    
    // Cancel any pending animation frame to avoid stacking calls
    if (animationFrame.current) {
      cancelAnimationFrame(animationFrame.current);
    }
    
    // Use requestAnimationFrame to batch layout reads and writes
    animationFrame.current = requestAnimationFrame(() => {
      if (!containerRef.current) return;
      
      const { left, width } = containerRef.current.getBoundingClientRect();
      let position = ((clientX - left) / width) * 100;
      
      position = Math.min(Math.max(position, 0), 100);
      setSliderPosition(position);
    });
  };

  const handleMove = (clientX: number) => {
    if (isDragging.current) {
      calculatePosition(clientX);
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    handleMove(e.clientX);
  };

  const handleTouchMove = (e: TouchEvent) => {
    handleMove(e.touches[0].clientX);
  };

  const stopDragging = () => {
    isDragging.current = false;
  };

  useEffect(() => {
    if (!isBrowser) return;
    globalThis.document.addEventListener("mousemove", handleMouseMove);
    globalThis.document.addEventListener("mouseup", stopDragging);
    globalThis.document.addEventListener("touchmove", handleTouchMove);
    globalThis.document.addEventListener("touchend", stopDragging);

    return () => {
      globalThis.document.removeEventListener("mousemove", handleMouseMove);
      globalThis.document.removeEventListener("mouseup", stopDragging);
      globalThis.document.removeEventListener("touchmove", handleTouchMove);
      globalThis.document.removeEventListener("touchend", stopDragging);
      
      // Clean up any pending animation frame
      if (animationFrame.current) {
        cancelAnimationFrame(animationFrame.current);
      }
    };
  }, []);

  if (import.meta.env.SSR) return null;

  return (
    <div 
      className="relative h-full w-full overflow-hidden rounded-xl shadow-md"
      ref={containerRef}
    >
      {/* After image (full width) */}
      <div className="h-full w-full">
        <img
          src={afterImage}
          alt="Fotografia imobiliária profissional finalizada em Balneário Camboriú — resultado Toc Toc"
          loading="lazy"
          decoding="async"
          width={1200}
          height={800}
          className="h-full w-full object-cover"
        />
        <div 
          className="absolute right-2 bottom-2 bg-toctoc-orange text-white text-xs font-medium px-2 py-1 rounded-md opacity-90"
        >
          Editado por IA Toc Toc
        </div>
        <Label className="absolute top-2 right-4 bg-toctoc-blue/80 text-white px-3 py-1 rounded-full text-sm">
          {afterLabel}
        </Label>
      </div>

      {/* Before image (partial width based on slider) */}
      <div 
        className="absolute top-0 left-0 h-full overflow-hidden"
        style={{ width: `${sliderPosition}%` }}
      >
        <img
          src={beforeImage}
          alt="Imagem original do imóvel em Balneário Camboriú antes da edição profissional da Toc Toc"
          loading="lazy"
          decoding="async"
          width={1200}
          height={800}
          className="h-full object-cover"
          style={{ width: `${100 / (sliderPosition / 100)}%` }}
        />
        <Label className="absolute top-2 left-4 bg-white/80 text-toctoc-gray-dark px-3 py-1 rounded-full text-sm">
          {beforeLabel}
        </Label>
      </div>

      {/* Slider control */}
      <div
        ref={handleRef}
        className="absolute top-0 bottom-0 w-1 bg-white shadow-md cursor-ew-resize"
        style={{ left: `calc(${sliderPosition}% - 0.5px)` }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-white shadow-md border-2 border-toctoc-blue flex items-center justify-center">
          <div className="flex flex-col items-center justify-center">
            <div className="w-1 h-4 rounded-full bg-toctoc-blue"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageComparisonSlider;
